import { useCallback, useMemo } from 'react'
import { Col, Row } from 'antd'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { TranslationKeys } from '../../Constants/TranslationKeys'
import { renderTranslation } from '../../Helpers/GenericHelpers'
import {
  EnableMovementLog,
  SizeFilterDefaultValue,
  MarketplaceDefaultValue,
  DefaultFromDate,
  AvailabilitiesSelectValue,
} from '../../Constants/Configuration'
import { setDateFrom, setDateTo } from '../../Slices/Kiosk/MovementsAvailabilitiesSlice'
import { createTimezoneCorrectedEpoch } from '../../Helpers/DateHelpers'
import { useAppDispatch, useAppSelector } from '../../hooks'
import DateTimeRangeFilter from 'Components/Generic/DateTimeFilter'

function DateTimeFilterMovements(props) {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const articleSelected = useAppSelector(state => state.present.articleDetail.articleSelected)
  const dateFrom = useAppSelector(state => state.present.movementsAvailabilities.value.dateFrom)
  const dateTo = useAppSelector(state => state.present.movementsAvailabilities.value.dateTo)
  const enableAllVariant = useAppSelector(state => state.present.quantities.value.enableAllVariant)
  const enableMovements = useAppSelector(state => state.present.movementsAvailabilities.value.enableMovements)
  const availabilitiesFilter = useAppSelector(state => state.present.movementsAvailabilities.value.availabilitiesFilter)
  const sizeSelect = useAppSelector(state => state.present.movementsAvailabilities.value.size)
  const selectedMarketplace = useAppSelector(state => state.present.movementsAvailabilities.value.selectedMarketplace)
  const movementTabVisible = useAppSelector(state => state.present.configuration.isTabMovementVisible)
  const workspaceMovementVisible = useAppSelector(state => state.present.configuration.workspaceMovementVisible)
  const availabilityTabVisible = useAppSelector(state => state.present.configuration.isTabAvailabilityVisibile)
  const workspaceAvailabilityVisible = useAppSelector(state => state.present.configuration.workspaceAvailabilityVisible)

  dayjs.extend(utc)
  dayjs.extend(timezone)

  const isArticleSelectedAndDataFetched =
    enableMovements === AvailabilitiesSelectValue
      ? articleSelected.fetchedAvailabilites
      : articleSelected.fetchedMovements

  const isLogFilterActive = useMemo(() => {
    return availabilitiesFilter.filter(filter => filter.value === EnableMovementLog && filter.active).length !== 0
  }, [availabilitiesFilter])

  const onDateFromChange = useCallback(
    date => {
      let dateToSave = dayjs(dateFrom).utc()
      const dateValue = date ?? dayjs(createTimezoneCorrectedEpoch(DefaultFromDate)).utc()
      dateToSave = dateToSave
        .set('year', dateValue.year())
        .set('month', dateValue.month())
        .set('date', dateValue.date())

      dispatch(setDateFrom(dateToSave.valueOf()))
      props.update()
    },
    [dateFrom, dispatch, props],
  )

  const onDateToChange = useCallback(
    date => {
      let dateToSave = dayjs(dateTo).utc()
      const dateValue = date ?? dayjs(createTimezoneCorrectedEpoch(undefined)).utc()
      dateToSave = dateToSave
        .set('year', dateValue.year())
        .set('month', dateValue.month())
        .set('date', dateValue.date())

      dispatch(setDateTo(dateToSave.valueOf()))

      if (enableMovements === AvailabilitiesSelectValue) {
        if (sizeSelect !== SizeFilterDefaultValue && selectedMarketplace !== MarketplaceDefaultValue) {
          props.update()
        }
      } else {
        props.update()
      }
    },
    [dateTo, dispatch, props, enableMovements, selectedMarketplace, sizeSelect],
  )

  const onTimeFromChange = useCallback(
    time => {
      let dateToSave = dayjs(dateFrom).utc()
      const timeValue = time ?? dayjs(createTimezoneCorrectedEpoch(undefined)).utc()
      dateToSave = dateToSave
        .set('hour', timeValue.hour())
        .set('minute', timeValue.minute())
        .set('second', timeValue.second())

      dispatch(setDateFrom(dateToSave.valueOf()))
      props.update()
    },
    [dateFrom, dispatch, props],
  )

  const onTimeToChange = useCallback(
    time => {
      let dateToSave = dayjs(dateTo).utc()
      const timeValue = time ?? dayjs(createTimezoneCorrectedEpoch(undefined)).utc()
      dateToSave = dateToSave
        .set('hour', timeValue.hour())
        .set('minute', timeValue.minute())
        .set('second', timeValue.second())

      dispatch(setDateTo(dateToSave.valueOf()))

      if (enableMovements === AvailabilitiesSelectValue) {
        if (sizeSelect !== SizeFilterDefaultValue && selectedMarketplace !== MarketplaceDefaultValue) {
          props.update()
        }
      } else {
        props.update()
      }
    },
    [dateTo, dispatch, props, sizeSelect, selectedMarketplace, enableMovements],
  )

  const fromClass = enableMovements === AvailabilitiesSelectValue ? ' d-none' : ''

  const disableElement =
    !isArticleSelectedAndDataFetched ||
    (articleSelected && enableAllVariant) ||
    (!(movementTabVisible && workspaceMovementVisible) &&
      !(availabilityTabVisible && workspaceAvailabilityVisible) &&
      isArticleSelectedAndDataFetched)

  const disabledPickerDateTo = current => {
    // Can not select days before dateFrom
    return current && (current < dateFrom || current > dayjs().startOf('day'))
  }

  const disabledPickerDateFrom = current => {
    return current && current > dateTo
  }

  return (
    <Col className='datetime-picker-container-movements'>
      <Row className='align-center'>
        <span
          className={
            'availabilities-from-label' +
            (isArticleSelectedAndDataFetched && !enableAllVariant ? '' : ' disable') +
            fromClass
          }
        >
          {renderTranslation(language, TranslationKeys.availabilitiesDateFilterFrom)}
        </span>
        <DateTimeRangeFilter
          dateClassName={'availabilities-from-date' + fromClass}
          timeClassName={'availabilities-from-time' + fromClass}
          showTime={isLogFilterActive}
          hideTo={true}
          dateFrom={dayjs(dateFrom).utc()}
          disableElement={disableElement}
          onDateFromChange={onDateFromChange}
          onTimeFromChange={onTimeFromChange}
          setDateFrom={dayjs(dateFrom).utc()}
          setDisableDateFrom={disabledPickerDateFrom}
        />
        <span
          className={
            'availabilities-to-label' +
            (props.hideFrom ? ' to-datetime-active' : '') +
            (isArticleSelectedAndDataFetched && !enableAllVariant ? '' : ' disable') +
            (enableMovements === AvailabilitiesSelectValue ? ' adj-avl-margin' : '')
          }
        >
          {renderTranslation(language, TranslationKeys.availabilitiesDateFilterTo)}
        </span>
        <DateTimeRangeFilter
          dateClassName={'availabilities-to-date'}
          timeClassName={'availabilities-from-time' + fromClass}
          showTime={isLogFilterActive}
          hideFrom={true}
          dateTo={dayjs(dateTo).utc()}
          setDateTo={dayjs(dateTo).utc()}
          disableElement={disableElement}
          onDateToChange={onDateToChange}
          onTimeToChange={onTimeToChange}
          setDisableDateTo={disabledPickerDateTo}
        />
      </Row>
    </Col>
  )
}

export default DateTimeFilterMovements
